<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowLeftIcon,
  UserCircleIcon,
  EnvelopeIcon,
  KeyIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/vue/24/outline'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import RadioInput from '@/components/common/inputs/RadioInput.vue'
import WysiwygInput from '@/components/common/inputs/WysiwygInput.vue'
import { useContactStore } from '@/stores/useContactStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import ServicesSyncPanel from '@/components/features/services/ServicesSyncPanel.vue'
import { useToast } from 'vue-toast-notification'
import { formatDate } from '@/utils/dateUtils'

const route = useRoute()
const router = useRouter()
const contactStore = useContactStore()
const boostCoreStore = useBoostCorePermissionStore()
const $toast = useToast()

const contactUuid = computed(() => route.params.contactUuid as string)
// Provenance fiche client (lien ouvert en nouvel onglet) : on masque le bouton « Retour ».
const hideBack = computed(() => route.query.from === 'client')
const isSuperAdmin = computed(() => boostCoreStore.isSuperAdmin)
const activeTab = ref<'infos' | 'emails' | 'tokens' | 'roles' | 'services'>('infos')

const form = ref({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  mobile: '',
  function: '',
  isSigner: false,
  comments: '',
  civility: undefined as 'Monsieur' | 'Madame' | undefined
})

const titles = [
  { id: 'Monsieur', value: 'Monsieur', label: 'Monsieur' },
  { id: 'Madame', value: 'Madame', label: 'Madame' }
]

const brevoEmails = ref<any[]>([])
const brevoCount = ref(0)
const isLoadingEmails = ref(false)
const resetTokens = ref<any[]>([])
const isLoadingTokens = ref(false)
const permissions = ref<any[]>([])
const permissionsForm = ref<{ companyUuid: string; companyName: string; codes: string[]; disabled: boolean }[]>([])
const isLoadingPermissions = ref(false)
const isSaving = ref(false)
const isSavingPermissions = ref(false)
const isSendingWelcome = ref(false)
const isSendingPasswordForgot = ref(false)
const isGeneratingTricksterToken = ref(false)
const twoFAStatus = ref<{ type: 'APP' | 'DISABLED' | null; disabledAt?: string } | null>(null)
const isLoading2FA = ref(false)
const isResetting2FA = ref(false)
const isDisabling2FA = ref(false)

const normalizePhone = (p: string) => {
  if (!p) return ''
  const cleaned = p.replace(/\s/g, '')
  if (cleaned.startsWith('+33')) {
    return '0' + cleaned.substring(3)
  }
  return cleaned
}

const formatPhone = (p: string) => {
  if (!p) return ''
  return `+33${p.replace(/\s/g, '').replace(/^0/, '')}`
}

const PORTAL_CODES = [
  { code: 'PORTAL_BILLING', label: 'Accès Facturation' },
  { code: 'PORTAL_QUOTES_READ', label: 'Accès Devis' },
  { code: 'PORTAL_ORDERS_READ', label: 'Accès Commandes & Livraisons' },
  { code: 'PORTAL_TICKETS', label: 'Accès Tickets' },
  { code: 'PORTAL_SERVICES_READ', label: 'Accès Services (lecture)' },
  { code: 'PORTAL_SERVICES_ADMIN', label: 'Accès Services (administration)' },
  { code: 'PORTAL_CONTACT_READ', label: 'Accès Lecture Contacts' },
  { code: 'PORTAL_CONTACT_ADMIN', label: 'Accès Admin Contacts' },
]

const contact = computed(() => contactStore.currentContact)

onMounted(async () => {
  if (!contactUuid.value) return
  const c = await contactStore.fetchContact(contactUuid.value)
  if (c) {
    form.value = {
      firstname: c.firstname ?? '',
      lastname: c.lastname ?? '',
      email: c.email ?? '',
      phone: normalizePhone(c.phone ?? ''),
      mobile: normalizePhone(c.mobile ?? ''),
      function: c.function ?? '',
      isSigner: c.isSigner ?? false,
      comments: c.comment ?? '',
      civility: c.civility ?? undefined
    }
  }
})

watch(activeTab, async (tab) => {
  if (!contactUuid.value) return
  if (tab === 'emails' && contact.value?.email) {
    isLoadingEmails.value = true
    const res = await contactStore.fetchBrevoEmails(contact.value.email, { limit: 50 })
    brevoEmails.value = res?.emails ?? []
    brevoCount.value = res?.count ?? 0
    isLoadingEmails.value = false
  } else if (tab === 'tokens') {
    isLoadingTokens.value = true
    isLoading2FA.value = true
    resetTokens.value = await contactStore.fetchResetTokens(contactUuid.value)
    twoFAStatus.value = await contactStore.fetch2FAStatus(contactUuid.value)
    isLoadingTokens.value = false
    isLoading2FA.value = false
  } else if (tab === 'roles') {
    isLoadingPermissions.value = true
    const data = await contactStore.fetchPermissions(contactUuid.value)
    permissions.value = data
    const contactCompanyUuid = contact.value?.company?.uuid ?? (contact.value as any)?.companyUuid
    const allEntries = (data ?? []).map((acc: any) => ({
      companyUuid: acc.companyUuid,
      companyName: acc.company?.name ?? 'Entreprise',
      codes: (acc.contactPortalPermission ?? []).map((cpp: any) => cpp.portalPermission?.code).filter(Boolean),
      disabled: false
    }))
    permissionsForm.value = contactCompanyUuid
      ? allEntries.filter((e: { companyUuid: string }) => e.companyUuid === contactCompanyUuid)
      : []
    if (contactCompanyUuid && permissionsForm.value.length === 0) {
      const companyName = contact.value?.company?.name ?? 'Entreprise'
      permissionsForm.value = [{
        companyUuid: contactCompanyUuid,
        companyName,
        codes: [],
        disabled: true
      }]
    }
    isLoadingPermissions.value = false
  }
}, { immediate: true })

function goBack() {
  // si historique > 2 page alors go back
  // > 2 car une ouverture de tab vierge correspond a une page d'historique
  if (window.history.length > 2) {
    router.back()
  } else {
    router.push('/dashboard/contacts')
  }
}

async function onSave() {
  if (!contactUuid.value) return
  isSaving.value = true
  const result = await contactStore.updateContact(contactUuid.value, {
    firstname: form.value.firstname,
    lastname: form.value.lastname,
    email: form.value.email,
    phone: formatPhone(form.value.phone) || undefined,
    mobile: formatPhone(form.value.mobile) || undefined,
    function: form.value.function || undefined,
    isSigner: form.value.isSigner,
    comments: form.value.comments || undefined,
    civility: form.value.civility
  })
  isSaving.value = false
  if (result.success) {
    $toast.success('Contact mis à jour.')
  } else {
    $toast.error(result.message)
  }
}

async function onSendWelcome() {
  if (!contactUuid.value) return
  isSendingWelcome.value = true
  const result = await contactStore.sendWelcomeEmail(contactUuid.value)
  isSendingWelcome.value = false
  if (result.success) {
    $toast.success('Email de bienvenue envoyé.')
    resetTokens.value = await contactStore.fetchResetTokens(contactUuid.value)
  } else {
    $toast.error(result.message)
  }
}

async function onSendPasswordForgot() {
  if (!contactUuid.value) return
  isSendingPasswordForgot.value = true
  const result = await contactStore.sendPasswordForgotEmail(contactUuid.value)
  isSendingPasswordForgot.value = false
  if (result.success) {
    $toast.success('Email mot de passe oublié envoyé.')
    resetTokens.value = await contactStore.fetchResetTokens(contactUuid.value)
  } else {
    $toast.error(result.message)
  }
}

async function onGenerateTricksterToken() {
  if (!contactUuid.value) return
  isGeneratingTricksterToken.value = true
  await contactStore.generateTricksterToken(contactUuid.value)
  isGeneratingTricksterToken.value = false
}

async function onReset2FA() {
  if (!contactUuid.value) return
  isResetting2FA.value = true
  const result = await contactStore.reset2FA(contactUuid.value)
  isResetting2FA.value = false
  if (result.success) {
    $toast.success('2FA réinitialisé.')
    twoFAStatus.value = await contactStore.fetch2FAStatus(contactUuid.value)
  } else {
    $toast.error(result.message)
  }
}

async function onDisable2FA() {
  if (!contactUuid.value) return
  isDisabling2FA.value = true
  const result = await contactStore.disable2FA(contactUuid.value)
  isDisabling2FA.value = false
  if (result.success) {
    $toast.success('2FA désactivé.')
    twoFAStatus.value = await contactStore.fetch2FAStatus(contactUuid.value)
  } else {
    $toast.error(result.message)
  }
}

function getReasonLabel(reason: string) {
  if (reason === 'WELCOME') return 'Bienvenue'
  if (reason === 'SELF_SERVICE') return 'Mot de passe oublié'
  return reason || '-'
}

function toggleAccess(companyUuid: string) {
  const entry = permissionsForm.value.find((p) => p.companyUuid === companyUuid)
  if (!entry) return
  entry.disabled = !entry.disabled
  if (entry.disabled) {
    entry.codes = []
  }
}

function togglePermission(companyUuid: string, code: string) {
  const entry = permissionsForm.value.find((p) => p.companyUuid === companyUuid)
  if (!entry || entry.disabled) return
  const idx = entry.codes.indexOf(code)
  if (idx >= 0) {
    entry.codes.splice(idx, 1)
  } else {
    entry.codes.push(code)
  }
}

function hasPermissionForCompany(companyUuid: string) {
  return permissionsForm.value.find((p) => p.companyUuid === companyUuid && !p.disabled) !== undefined
}

function hasPermissionForCode(companyUuid: string, code: string) {
  return permissionsForm.value.find((p) => p.companyUuid === companyUuid)?.codes.includes(code) ?? false
}

async function onSavePermissions() {
  if (!contactUuid.value) return
  isSavingPermissions.value = true
  const payload = permissionsForm.value
  .filter((p) => !p.disabled)
  .map((p) => ({
    companyUuid: p.companyUuid,
    codes: p.codes
  }))
  const result = await contactStore.setPermissions(contactUuid.value, payload)
  isSavingPermissions.value = false
  if (result.success) {
    $toast.success('Permissions mises à jour.')
    permissions.value = await contactStore.fetchPermissions(contactUuid.value)
    permissionsForm.value = (permissions.value ?? []).map((acc: any) => ({
      companyUuid: acc.companyUuid,
      companyName: acc.company?.name ?? 'Entreprise',
      codes: (acc.contactPortalPermission ?? []).map((cpp: any) => cpp.portalPermission?.code).filter(Boolean),
      disabled: false
    }))

    const contactCompanyUuid = contact.value?.company?.uuid ?? (contact.value as any)?.companyUuid
    if (contactCompanyUuid && permissionsForm.value.length === 0) {
      const companyName = contact.value?.company?.name ?? 'Entreprise'
      permissionsForm.value = [{
        companyUuid: contactCompanyUuid,
        companyName,
        codes: [],
        disabled: true
      }]
    }
  } else {
    $toast.error(result.message)
  }
}
</script>

<template>
  <div class="min-h-screen bg-base-200/40">
    <div class="px-4 py-4 max-w-[900px] mx-auto w-full">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-1">
          <ButtonDaisy v-if="!hideBack" text="Retour" variant="ghost" :icon="ArrowLeftIcon" @click="goBack" />
          <HelpButton />
        </div>
      </div>

      <div v-if="contactStore.isLoadingContact" class="mt-6 card bg-base-100 shadow">
        <div class="card-body">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      </div>

      <div v-else-if="contact" class="mt-6">
        <div role="tablist" class="tabs tabs-boxed gap-2 bg-base-300 p-1.5 rounded-xl mb-6 shadow-inner">
          <button
            role="tab"
            class="tab gap-2 transition-all duration-300 !h-10 rounded-xl hover:bg-base-100/50"
            :class="{
              'tab-active !bg-primary !text-primary-content shadow-md scale-[1.02]': activeTab === 'infos',
              'hover:text-primary': activeTab !== 'infos'
            }"
            @click="activeTab = 'infos'"
          >
            <UserCircleIcon class="w-4 h-4" />
            <span class="font-medium text-xs md:text-sm">Informations</span>
          </button>
          <button
            role="tab"
            class="tab gap-2 transition-all duration-300 !h-10 rounded-xl hover:bg-base-100/50"
            :class="{
              'tab-active !bg-primary !text-primary-content shadow-md scale-[1.02]': activeTab === 'emails',
              'hover:text-primary': activeTab !== 'emails'
            }"
            @click="activeTab = 'emails'"
          >
            <EnvelopeIcon class="w-4 h-4" />
            <span class="font-medium text-xs md:text-sm">Emails Brevo</span>
          </button>
          <button
            role="tab"
            class="tab gap-2 transition-all duration-300 !h-10 rounded-xl hover:bg-base-100/50"
            :class="{
              'tab-active !bg-primary !text-primary-content shadow-md scale-[1.02]': activeTab === 'tokens',
              'hover:text-primary': activeTab !== 'tokens'
            }"
            @click="activeTab = 'tokens'"
          >
            <KeyIcon class="w-4 h-4" />
            <span class="font-medium text-xs md:text-sm">Accès Portail</span>
          </button>
          <button
            role="tab"
            class="tab gap-2 transition-all duration-300 !h-10 rounded-xl hover:bg-base-100/50"
            :class="{
              'tab-active !bg-primary !text-primary-content shadow-md scale-[1.02]': activeTab === 'roles',
              'hover:text-primary': activeTab !== 'roles'
            }"
            @click="activeTab = 'roles'"
          >
            <ShieldCheckIcon class="w-4 h-4" />
            <span class="font-medium text-xs md:text-sm">Rôles</span>
          </button>
          <button
            v-if="isSuperAdmin"
            role="tab"
            class="tab gap-2 transition-all duration-300 !h-10 rounded-xl hover:bg-base-100/50"
            :class="{
              'tab-active !bg-warning !text-warning-content shadow-md scale-[1.02]': activeTab === 'services',
              'hover:text-warning': activeTab !== 'services'
            }"
            @click="activeTab = 'services'"
          >
            <WrenchScrewdriverIcon class="w-4 h-4" />
            <span class="font-medium text-xs md:text-sm">Services (Super Admin)</span>
          </button>
        </div>

        <div v-if="activeTab === 'infos'" class="card bg-base-100 shadow-sm border border-base-200 overflow-hidden">
          <div class="card-body p-4 md:p-6">
            <form @submit.prevent="onSave" class="space-y-6">
              <div class="flex items-center justify-between flex-wrap gap-4 border-b pb-4 mb-6">
                <div>
                  <h2 class="card-title">Informations du contact</h2>
                  <p class="text-sm text-base-content/60">Gérer les détails personnels et professionnels du contact.</p>
                </div>

                <ButtonDaisy
                  text="Enregistrer"
                  variant="primary"
                  @click="$emit('save')"
                />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                <!-- Civilité -->
                <div class="col-span-1 md:col-span-2">
                  <RadioInput
                    label="Civilité *"
                    :options="titles || []"
                    v-model="form.civility"
                    itemClasses="flex-wrap"
                    :disabled="isSaving"
                    labelClasses="block text-xs font-bold uppercase text-base-content/60 mb-1"
                  />
                </div>

                <!-- Prénom -->
                <div>
                  <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Prénom *</label>
                  <input v-model="form.firstname" type="text" placeholder="Prénom"
                         class="input input-bordered input-sm w-full" :disabled="isSaving" required />
                </div>

                <!-- Nom -->
                <div>
                  <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Nom *</label>
                  <input v-model="form.lastname" type="text" placeholder="Nom"
                         class="input input-bordered input-sm w-full" :disabled="isSaving" required />
                </div>

                <!-- Email -->
                <div class="col-span-1 md:col-span-2">
                  <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Email *</label>
                  <input v-model="form.email" type="email" placeholder="email@exemple.com"
                         class="input input-bordered input-sm w-full" :disabled="isSaving" required />
                </div>

                <!-- Téléphone fixe -->
                <div>
                  <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Téléphone fixe</label>
                  <div class="join w-full">
                    <span
                      class="btn btn-sm join-item no-animation bg-base-200 pointer-events-none">+33</span>
                    <input v-model="form.phone" type="tel" placeholder="01 00 00 00 00"
                           class="input input-bordered input-sm join-item w-full" :disabled="isSaving" />
                  </div>
                </div>

                <!-- Mobile -->
                <div>
                  <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Mobile</label>
                  <div class="join w-full">
                    <span
                      class="btn btn-sm join-item no-animation bg-base-200 pointer-events-none">+33</span>
                    <input v-model="form.mobile" type="tel" placeholder="06 00 00 00 00"
                           class="input input-bordered input-sm join-item w-full" :disabled="isSaving" />
                  </div>
                </div>

                <!-- Fonction -->
                <div>
                  <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Fonction</label>
                  <input v-model="form.function" type="text" placeholder="Ex: Responsable Achats"
                         class="input input-bordered input-sm w-full" :disabled="isSaving" />
                </div>

                <!-- Signataire -->
                <div class="col-span-1 md:col-span-2">
                  <div class="flex items-center gap-4">
                    <label class="text-xs font-bold uppercase text-base-content/60">Signataire</label>
                    <div class="flex items-center">
                      <input v-model="form.isSigner" type="checkbox" class="toggle toggle-primary toggle-sm"
                             :disabled="isSaving" />
                      <span class="ml-2 text-sm">{{ form.isSigner ? 'Oui' : 'Non' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Commentaire -->
                <div class="col-span-1 md:col-span-2 border-t mt-1 pt-3">
                  <WysiwygInput
                    name="comments"
                    label="Commentaire"
                    v-model="form.comments"
                    :disabled="isSaving"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>

        <div v-else-if="activeTab === 'emails'" class="card bg-base-100 shadow-sm border border-base-200">
          <div class="card-body p-4 md:p-6">
            <div class="flex items-center justify-between flex-wrap gap-4 border-b pb-4 mb-6">
              <div>
                <h2 class="card-title">Emails envoyés via Brevo</h2>
                <p class="text-sm text-base-content/60">
                  Emails transactionnels envoyés à {{ contact.email }}
                </p>
              </div>
            </div>

            <div v-if="isLoadingEmails" class="py-8">
              <span class="loading loading-spinner loading-lg"></span>
            </div>
            <div v-else-if="brevoEmails.length === 0" class="py-8 text-center text-base-content/60">
              Aucun email trouvé.
            </div>
            <div v-else class="overflow-x-auto mt-4">
              <table class="table table-zebra">
                <thead>
                <tr>
                  <th>Date</th>
                  <th>Sujet</th>
                  <th>Message ID</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="e in brevoEmails" :key="e.uuid">
                  <td>{{ (e.date ?? e.sentAt) ? formatDate(e.date ?? e.sentAt) : '-' }}</td>
                  <td>{{ e.subject ?? '-' }}</td>
                  <td class="font-mono text-xs">{{ e.messageId ?? '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-else-if="activeTab === 'tokens'" class="card bg-base-100 shadow-sm border border-base-200">
          <div class="card-body p-4 md:p-6">
            <div class="flex flex-col gap-6">
              <div class="flex items-center justify-between flex-wrap gap-4 border-b pb-4 mb-2">
                <div>
                  <h2 class="card-title">Accès Portail</h2>
                  <p class="text-sm text-base-content/60">Gérer les accès et envoyer les emails de connexion.</p>
                </div>
                <ButtonDaisy
                  text="Se connecter"
                  variant="secondary"
                  :icon="ArrowRightOnRectangleIcon"
                  :loader="isGeneratingTricksterToken"
                  :disabled="!contact.uuid"
                  @click="onGenerateTricksterToken"
                />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="p-4 bg-base-200/50 rounded-lg flex flex-col justify-between gap-3">
                  <div>
                    <h3 class="font-semibold text-sm mb-1">Premier accès au portail</h3>
                    <p class="text-xs text-base-content/60">Lien vers l'initialisation du mot de passe.</p>
                  </div>
                  <ButtonDaisy
                    text="Email 1ère connexion"
                    variant="primary"
                    class="w-full"
                    :loader="isSendingWelcome"
                    :disabled="!contact.email"
                    @click="onSendWelcome"
                  />
                </div>

                <div class="p-4 bg-base-200/50 rounded-lg flex flex-col justify-between gap-3">
                  <div>
                    <h3 class="font-semibold text-sm mb-1">Soucis de connexion</h3>
                    <p class="text-xs text-base-content/60">Envoyer un lien de réinitialisation de mot de passe.</p>
                  </div>
                  <ButtonDaisy
                    text="Mot de passe oublié"
                    variant="secondary"
                    class="w-full"
                    :loader="isSendingPasswordForgot"
                    :disabled="!contact.email"
                    @click="onSendPasswordForgot"
                  />
                </div>
              </div>
            </div>
            <div v-if="contact.email" class="mt-6 p-4 bg-base-200/50 rounded-lg">
              <h3 class="font-semibold mb-2">2FA</h3>
              <div v-if="isLoading2FA" class="text-sm text-base-content/60">Chargement...</div>
              <div v-else class="flex flex-wrap items-center gap-4">
                <span class="text-sm">
                  Statut :
                  <span v-if="twoFAStatus?.type === 'APP'" class="badge badge-success">Activé (APP)</span>
                  <span v-else-if="twoFAStatus?.type === 'DISABLED'" class="badge badge-warning">Désactivé</span>
                  <span v-else class="badge badge-ghost">Aucun</span>
                </span>
                <div class="flex gap-2">
                  <ButtonDaisy
                    text="Réinitialiser 2FA"
                    variant="outline"
                    :loader="isResetting2FA"
                    :disabled="!twoFAStatus?.type"
                    @click="onReset2FA"
                  />
                  <ButtonDaisy
                    text="Désactiver 2FA"
                    variant="outline"
                    :loader="isDisabling2FA"
                    :disabled="twoFAStatus?.type !== 'APP'"
                    @click="onDisable2FA"
                  />
                </div>
              </div>
            </div>
            <div v-if="isLoadingTokens" class="py-8">
              <span class="loading loading-spinner loading-lg"></span>
            </div>
            <div v-else-if="resetTokens.length === 0" class="py-8 text-center text-base-content/60">
              Aucun token de réinitialisation.
            </div>
            <div v-else class="overflow-x-auto mt-4">
              <table class="table table-zebra">
                <thead>
                <tr>
                  <th>Type</th>
                  <th>Demandé le</th>
                  <th>Expire le</th>
                  <th>Utilisé</th>
                  <th>Révoqué</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="t in resetTokens" :key="t.uuid">
                  <td>{{ getReasonLabel(t.requestReason) }}</td>
                  <td>{{ formatDate(t.requestedAt) }}</td>
                  <td>{{ formatDate(t.expiresAt) }}</td>
                  <td>{{ t.usedAt ? formatDate(t.usedAt) : '-' }}</td>
                  <td>{{ t.revokedAt ? formatDate(t.revokedAt) : '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <ServicesSyncPanel
          v-else-if="activeTab === 'services' && isSuperAdmin"
          :company-uuid="contact?.company?.uuid ?? (contact as any)?.companyUuid"
        />

        <div v-else-if="activeTab === 'roles'" class="card bg-base-100 shadow-sm border border-base-200">
          <div class="card-body p-4 md:p-6">
            <div class="flex items-center justify-between flex-wrap gap-4 border-b pb-4 mb-6">
              <div>
                <h2 class="card-title">Permissions portail client</h2>
                <p class="text-sm text-base-content/60">
                  Configurez les accès au portail pour la société du contact.
                </p>
              </div>
              <ButtonDaisy
                text="Enregistrer"
                variant="primary"
                :loader="isSavingPermissions"
                :disabled="isLoadingPermissions"
                @click="onSavePermissions"
              />
            </div>
            <div v-if="isLoadingPermissions" class="py-8">
              <span class="loading loading-spinner loading-lg mx-auto block"></span>
            </div>
            <div v-else class="space-y-4 mt-4">
              <div
                v-for="entry in permissionsForm"
                :key="entry.companyUuid"
                class="p-4 bg-base-200/50 rounded-lg"
              >
                <div class="flex flex-wrap gap-4 mt-3">
                  <label
                    class="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm checkbox-primary"
                      :checked="hasPermissionForCompany(entry.companyUuid)"
                      @change="toggleAccess(entry.companyUuid)"
                    />
                    <span class="text-sm">{{ entry.companyName }}</span>
                  </label>
                </div>
                <div class="flex flex-wrap gap-4 mt-3" :class="{ 'opacity-50 pointer-events-none': entry.disabled }">
                  <label
                    v-for="pc in PORTAL_CODES"
                    :key="pc.code"
                    class="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm checkbox-primary"
                      :checked="hasPermissionForCode(entry.companyUuid, pc.code)"
                      :disabled="entry.disabled"
                      @change="togglePermission(entry.companyUuid, pc.code)"
                    />
                    <span class="text-sm">{{ pc.label }}</span>
                  </label>
                </div>
              </div>
              <div v-if="permissionsForm.length === 0 && !isLoadingPermissions"
                   class="py-8 text-center text-base-content/60">
                Ce contact n'a pas de société associée.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
